import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useAuth } from '../lib/auth'
import { api, APIError } from '../lib/api'
import { UserTable, type UserRow } from '../components/UserTable'
import { CreateUserDialog } from '../components/CreateUserDialog'
import { CSVImportDialog } from '../components/CSVImportDialog'

export const Route = createFileRoute('/admin/users')({
  beforeLoad: async () => {
    const state = useAuth.getState()
    if (state.loading) {
      await state.load()
    }
    const user = useAuth.getState().user
    if (!user) {
      throw redirect({ to: '/login' })
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/profile' })
    }
  },
  component: AdminUsersPage,
})

function AdminUsersPage() {
  const [users, setUsers] = useState<UserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)

  async function refresh() {
    setLoading(true)
    setError(null)
    try {
      const list = await api<UserRow[]>('/api/admin/users')
      setUsers(list)
    } catch (err) {
      if (err instanceof APIError && err.status === 403) {
        setError('Нет прав на просмотр пользователей')
      } else {
        setError('Не удалось загрузить пользователей')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  return (
    <div className="max-w-6xl mx-auto p-8 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Пользователи
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => setImportOpen(true)}
            className="px-3 py-1.5 text-sm border border-zinc-300 dark:border-zinc-600 rounded-md text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            📥 Импорт CSV
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md"
          >
            + Добавить
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {loading ? (
        <p className="text-sm text-zinc-500">Загрузка…</p>
      ) : (
        <UserTable users={users} onChanged={() => void refresh()} />
      )}

      {createOpen && (
        <CreateUserDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false)
            void refresh()
          }}
        />
      )}
      {importOpen && (
        <CSVImportDialog
          onClose={() => setImportOpen(false)}
          onImported={() => {
            setImportOpen(false)
            void refresh()
          }}
        />
      )}
    </div>
  )
}
